import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import OrganizerLayout from '../../components/layouts/OrganizerLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { 
  Camera, Plus, FolderPlus, Image, Trash2, Loader2, Upload, 
  MoreVertical, Heart, Download, X, ChevronLeft, ChevronRight,
  Images, Pencil, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizerGallery = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [albums, setAlbums] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Dialogs
  const [albumDialogOpen, setAlbumDialogOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [photoUrls, setPhotoUrls] = useState({});
  
  // Form data
  const [albumForm, setAlbumForm] = useState({
    name: '',
    description: '',
    is_public: true
  });
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      fetchAlbums();
      fetchPhotos();
    }
  }, [selectedEventId, selectedAlbum]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEventId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gallery/albums?event_id=${selectedEventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlbums(response.data);
    } catch (error) {
      console.error('Failed to fetch albums:', error);
    }
  };

  const fetchPhotos = async () => {
    try {
      const url = selectedAlbum 
        ? `${API_URL}/api/gallery/photos?event_id=${selectedEventId}&album_id=${selectedAlbum.id}`
        : `${API_URL}/api/gallery/photos?event_id=${selectedEventId}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(response.data.photos || []);
      
      // Load photo URLs
      for (const photo of response.data.photos || []) {
        loadPhotoUrl(photo);
      }
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    }
  };

  const loadPhotoUrl = async (photo) => {
    if (photoUrls[photo.id]) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/gallery/photos/${photo.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const blobUrl = URL.createObjectURL(response.data);
      setPhotoUrls(prev => ({ ...prev, [photo.id]: blobUrl }));
    } catch (error) {
      console.error(`Failed to load photo ${photo.id}:`, error);
    }
  };

  const handleCreateAlbum = async () => {
    try {
      const endpoint = editingAlbum 
        ? `${API_URL}/api/gallery/albums/${editingAlbum.id}`
        : `${API_URL}/api/gallery/albums?event_id=${selectedEventId}`;
      
      const method = editingAlbum ? 'put' : 'post';
      
      await axios[method](endpoint, albumForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(editingAlbum ? 'Album updated!' : 'Album created!');
      setAlbumDialogOpen(false);
      setEditingAlbum(null);
      setAlbumForm({ name: '', description: '', is_public: true });
      fetchAlbums();
    } catch (error) {
      toast.error('Failed to save album');
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    if (!window.confirm('Delete this album and all its photos?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/gallery/albums/${albumId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Album deleted');
      if (selectedAlbum?.id === albumId) {
        setSelectedAlbum(null);
      }
      fetchAlbums();
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete album');
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    if (files.length > 20) {
      toast.error('Maximum 20 photos per upload');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('event_id', selectedEventId);
      if (selectedAlbum) {
        formData.append('album_id', selectedAlbum.id);
      }
      files.forEach(file => formData.append('files', file));
      
      const response = await axios.post(
        `${API_URL}/api/gallery/photos/bulk-upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        }
      );
      
      const { total_uploaded, total_failed } = response.data;
      
      if (total_uploaded > 0) {
        toast.success(`${total_uploaded} photo(s) uploaded successfully`);
      }
      if (total_failed > 0) {
        toast.error(`${total_failed} photo(s) failed to upload`);
      }
      
      fetchPhotos();
      fetchAlbums();
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API_URL}/api/gallery/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo deleted');
      
      // Revoke blob URL
      if (photoUrls[photoId]) {
        URL.revokeObjectURL(photoUrls[photoId]);
        setPhotoUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[photoId];
          return newUrls;
        });
      }
      
      fetchPhotos();
      fetchAlbums();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleLikePhoto = async (photoId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/gallery/photos/${photoId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPhotos(prev => prev.map(p => 
        p.id === photoId ? { ...p, like_count: response.data.like_count } : p
      ));
    } catch (error) {
      toast.error('Failed to like photo');
    }
  };

  const openEditAlbum = (album) => {
    setEditingAlbum(album);
    setAlbumForm({
      name: album.name,
      description: album.description || '',
      is_public: album.is_public
    });
    setAlbumDialogOpen(true);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < photos.length) {
      setLightboxIndex(newIndex);
    }
  };

  if (loading) {
    return (
      <OrganizerLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </OrganizerLayout>
    );
  }

  return (
    <OrganizerLayout>
      <div className="space-y-6" data-testid="organizer-gallery">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Camera className="h-6 w-6 text-maroon" />
              Photo Gallery
            </h1>
            <p className="text-gray-500">Manage reunion photos and albums</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger className="w-[200px]" data-testid="event-select">
                <SelectValue placeholder="Select event" />
              </SelectTrigger>
              <SelectContent>
                {events.map(event => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.event_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-wrap gap-3">
          <Button 
            onClick={() => {
              setEditingAlbum(null);
              setAlbumForm({ name: '', description: '', is_public: true });
              setAlbumDialogOpen(true);
            }}
            className="bg-maroon hover:bg-maroon-dark"
            data-testid="create-album-btn"
          >
            <FolderPlus className="h-4 w-4 mr-2" />
            Create Album
          </Button>
          
          <Button 
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-photos-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading {uploadProgress}%
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload Photos
              </>
            )}
          </Button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            data-testid="photo-file-input"
          />
        </div>

        {/* Albums Grid */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Images className="h-5 w-5" />
              Albums
            </CardTitle>
            <CardDescription>
              Organize photos into albums
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {/* All Photos "Album" */}
              <div 
                className={`relative group cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                  !selectedAlbum ? 'border-maroon ring-2 ring-maroon/20' : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedAlbum(null)}
                data-testid="all-photos-album"
              >
                <div className="aspect-square bg-gradient-to-br from-maroon/20 to-gold/20 flex items-center justify-center">
                  <Images className="h-12 w-12 text-maroon" />
                </div>
                <div className="p-3 bg-white">
                  <p className="font-medium text-sm truncate">All Photos</p>
                  <p className="text-xs text-gray-500">{photos.length} photos</p>
                </div>
              </div>

              {/* Album Cards */}
              {albums.map(album => (
                <div 
                  key={album.id}
                  className={`relative group cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                    selectedAlbum?.id === album.id ? 'border-maroon ring-2 ring-maroon/20' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedAlbum(album)}
                  data-testid={`album-${album.id}`}
                >
                  <div className="aspect-square bg-gray-100 flex items-center justify-center">
                    {album.cover_photo && photoUrls[album.cover_photo.id] ? (
                      <img 
                        src={photoUrls[album.cover_photo.id]} 
                        alt={album.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Image className="h-12 w-12 text-gray-400" />
                    )}
                  </div>
                  <div className="p-3 bg-white">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm truncate">{album.name}</p>
                      {!album.is_public && <EyeOff className="h-3 w-3 text-gray-400" />}
                    </div>
                    <p className="text-xs text-gray-500">{album.photo_count} photos</p>
                  </div>
                  
                  {/* Album Actions */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                        <Button size="icon" variant="secondary" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditAlbum(album); }}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={(e) => { e.stopPropagation(); handleDeleteAlbum(album.id); }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Photos Grid */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              {selectedAlbum ? selectedAlbum.name : 'All Photos'}
            </CardTitle>
            <CardDescription>
              {selectedAlbum 
                ? `${selectedAlbum.photo_count} photos in this album`
                : `${photos.length} photos total`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {photos.length === 0 ? (
              <div className="text-center py-12">
                <Camera className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No photos yet</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Photos
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {photos.map((photo, index) => (
                  <div 
                    key={photo.id}
                    className="relative group aspect-square rounded-lg overflow-hidden cursor-pointer"
                    onClick={() => openLightbox(index)}
                    data-testid={`photo-${photo.id}`}
                  >
                    {photoUrls[photo.id] ? (
                      <img 
                        src={photoUrls[photo.id]} 
                        alt={photo.caption || 'Photo'}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                      </div>
                    )}
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all">
                      <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex items-center justify-between text-white text-xs">
                          <div className="flex items-center gap-1">
                            <Heart className="h-3 w-3" />
                            {photo.like_count || 0}
                          </div>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-6 w-6 text-white hover:text-red-400"
                            onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Album Dialog */}
        <Dialog open={albumDialogOpen} onOpenChange={setAlbumDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingAlbum ? 'Edit Album' : 'Create Album'}</DialogTitle>
              <DialogDescription>
                {editingAlbum ? 'Update album details' : 'Create a new photo album'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Album Name</Label>
                <Input 
                  value={albumForm.name}
                  onChange={(e) => setAlbumForm({ ...albumForm, name: e.target.value })}
                  placeholder="e.g., Grand Reunion 2026"
                  data-testid="album-name-input"
                />
              </div>
              
              <div>
                <Label>Description (Optional)</Label>
                <Textarea 
                  value={albumForm.description}
                  onChange={(e) => setAlbumForm({ ...albumForm, description: e.target.value })}
                  placeholder="Add a description..."
                  rows={3}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Public Album</Label>
                  <p className="text-xs text-gray-500">Visible to all attendees</p>
                </div>
                <Switch 
                  checked={albumForm.is_public}
                  onCheckedChange={(checked) => setAlbumForm({ ...albumForm, is_public: checked })}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setAlbumDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateAlbum}
                disabled={!albumForm.name.trim()}
                className="bg-maroon hover:bg-maroon-dark"
              >
                {editingAlbum ? 'Save Changes' : 'Create Album'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Lightbox */}
        {lightboxOpen && photos[lightboxIndex] && (
          <div 
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            <Button 
              size="icon" 
              variant="ghost" 
              className="absolute top-4 right-4 text-white hover:bg-white/10"
              onClick={() => setLightboxOpen(false)}
            >
              <X className="h-6 w-6" />
            </Button>
            
            <Button 
              size="icon" 
              variant="ghost" 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10"
              onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
              disabled={lightboxIndex === 0}
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
            
            <div className="max-w-4xl max-h-[80vh] px-16" onClick={e => e.stopPropagation()}>
              {photoUrls[photos[lightboxIndex].id] && (
                <img 
                  src={photoUrls[photos[lightboxIndex].id]}
                  alt={photos[lightboxIndex].caption || 'Photo'}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              )}
              
              {photos[lightboxIndex].caption && (
                <p className="text-white text-center mt-4">{photos[lightboxIndex].caption}</p>
              )}
              
              <div className="flex items-center justify-center gap-4 mt-4">
                <Button 
                  variant="ghost" 
                  className="text-white hover:bg-white/10"
                  onClick={() => handleLikePhoto(photos[lightboxIndex].id)}
                >
                  <Heart className="h-5 w-5 mr-2" />
                  {photos[lightboxIndex].like_count || 0}
                </Button>
                
                <span className="text-white/60 text-sm">
                  {lightboxIndex + 1} / {photos.length}
                </span>
              </div>
            </div>
            
            <Button 
              size="icon" 
              variant="ghost" 
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10"
              onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
              disabled={lightboxIndex === photos.length - 1}
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          </div>
        )}
      </div>
    </OrganizerLayout>
  );
};

export default OrganizerGallery;
